import * as THREE from 'three';
import { FAVOR_GIVERS, favorsFrom, type FavorDef } from '../content/favors';
import { possessive } from '../content/jobs';
import type { Scene } from '../content/types';
import type { NpcAgent } from '../entities/NpcManager';
import { usedToday, useToday } from '../state/daily';
import type { Interactions } from '../systems/Interactions';
import { carryThing, glint, lostThing } from '../world/builders/hustleProps';
import { HIGHWAY, inRect, PARK_BOUNDS, type Vec2 } from '../world/layout';
import { roomSlotAt } from '../world/rooms';
import type { Activities } from './Activities';
import type { GameServices } from './services';

export const FAVORS_PER_DAY = 8;
/** How close somebody has to be to wave you over. */
const OFFER_RANGE = 28;
/** Real seconds they'll wait for an answer. */
const OFFER_SECONDS = 70;
/** Game minutes to get it done once you've said yes (about two real minutes). */
const FAVOR_MINUTES = 8 * 60;
const FAR = { x: 1e6, z: 1e6 };

interface Active {
  def: FavorDef;
  giver: NpcAgent;
  pay: number;
  deadline: number;
  /** start: not yet; holding: you've got the thing (found it, picked it up, carrying the errand). */
  stage: 'start' | 'holding';
  prop: THREE.Group | null;
  sparkle: THREE.Sprite | null;
  spot: Vec2 | null;
}

/**
 * Favors: the quickest money in Lucky Acres. Every so often a neighbor near you waves you
 * over (a dollar sign over their head). Say yes, do the thing (fetch a beer, find their teeth, haul a
 * cooler, smack an AC unit, run a casserole across the park) and they pay you on the spot.
 */
export class Favors {
  private offer: { def: FavorDef; agent: NpcAgent; until: number } | null = null;
  private active: Active | null = null;
  private timer = 12;
  private readonly tmp = new THREE.Vector3();

  constructor(
    private readonly s: GameServices,
    act: Activities,
  ) {
    act.talkOverrides.push((agent) => this.talk(agent));
  }

  get current(): FavorDef | null {
    return this.active?.def ?? null;
  }

  register(interactions: Interactions): void {
    interactions.add({
      id: 'favor-item',
      at: () => this.active?.spot ?? FAR,
      radius: 1.5,
      priority: 1,
      prompt: () => {
        const a = this.active;
        if (!a || a.stage !== 'start' || !a.prop || (a.def.kind !== 'find' && a.def.kind !== 'carry'))
          return null;
        return a.def.kind === 'find'
          ? `Pick up ${possessive(a.giver.def.name)} ${a.def.item?.name}`
          : `Pick up the ${a.def.item?.name}`;
      },
      action: () => this.pickUp(),
      highlight: () => this.active?.prop ?? null,
      icon: ':high-five:',
    });
    interactions.add({
      id: 'favor-fix',
      at: () => (this.active?.def.kind === 'fix' ? (this.active.def.at ?? FAR) : FAR),
      radius: 1.6,
      priority: 1,
      prompt: () => (this.active?.def.kind === 'fix' ? (this.active.def.verb ?? 'Fix it') : null),
      action: () => this.fix(),
      icon: ':wrench:',
    });
  }

  /** New game, load, back to the title: nobody's asking for anything. */
  reset(): void {
    this.clearOffer();
    this.drop();
    this.timer = 12;
  }

  private clearOffer(): void {
    this.offer = null;
    this.s.ui.hud.removeTag('favor-offer');
  }

  private drop(): void {
    const a = this.active;
    if (!a) return;
    if (a.prop && this.s.player.carrying === a.prop) this.s.player.setCarrying(null);
    a.prop?.removeFromParent();
    a.sparkle?.removeFromParent();
    this.active = null;
    this.s.ui.hud.removeTag('favor-target');
  }

  update(dt: number, time: number, allowNew: boolean): void {
    const { store, player, ui } = this.s;
    const p = player.position;
    const outdoors = roomSlotAt(p.x) < 0;

    // Somebody's waving you over.
    const o = this.offer;
    if (o) {
      const gone =
        this.s.now() > o.until ||
        !o.agent.active ||
        o.agent.hold !== null ||
        o.agent.position.distanceTo(p) > 60 ||
        this.active !== null;
      if (gone) {
        this.clearOffer();
        this.timer = 20;
      } else ui.hud.setTag('favor-offer', ':dollar: Needs a hand', this.head(o.agent), 'favor-tag');
    } else if (!this.active && allowNew && outdoors) {
      this.timer -= dt;
      const hour = store.minutes / 60;
      if (this.timer <= 0 && hour >= 8 && hour < 21 && usedToday(store, 'favors') < FAVORS_PER_DAY) {
        this.timer = this.s.rng.range(22, 40);
        this.makeOffer();
      }
    }

    const a = this.active;
    if (!a) return;
    if (store.now > a.deadline || !a.giver.active) {
      store.toast(
        a.giver.active
          ? `Took too long. ${a.giver.def.name} figured it out without you.`
          : `${a.giver.def.name} turned in for the night. Favor's off.`,
        'bad',
        ':raise-hand:',
      );
      this.drop();
      return;
    }
    if (a.sparkle) {
      const pulse = 0.5 + 0.5 * Math.sin(time * 5);
      a.sparkle.scale.setScalar(0.35 + pulse * 0.5);
    }
    // Carried it there: set it down, get paid.
    if (a.def.kind === 'carry' && a.stage === 'holding' && a.def.at) {
      if (player.carrying !== a.prop) {
        store.toast(
          `You put the ${a.def.item?.name} down somewhere. ${a.giver.def.name} sighs.`,
          'bad',
          ':box:',
        );
        this.drop();
        return;
      }
      if (Math.hypot(p.x - a.def.at.x, p.z - a.def.at.z) < 2.2) {
        player.setCarrying(null);
        a.prop?.removeFromParent();
        this.s.sfx('thud');
        this.finish();
        return;
      }
    }
    // Who you're headed to gets a little tag.
    const target = this.targetAgent();
    if (target)
      ui.hud.setTag(
        'favor-target',
        a.def.kind === 'errand' ? (a.def.item?.icon ?? ':raise-hand:') : ':raise-hand:',
        this.head(target),
        'favor-tag',
      );
    else ui.hud.removeTag('favor-target');
  }

  private head(agent: NpcAgent): THREE.Vector3 {
    return this.tmp.copy(agent.position).setY(2.55 * agent.def.look.height);
  }

  private targetAgent(): NpcAgent | null {
    const a = this.active;
    if (!a) return null;
    if (a.def.kind === 'errand') return this.s.npcs.get(a.def.to ?? '') ?? null;
    if (a.def.kind === 'beer' || (a.def.kind === 'find' && a.stage === 'holding')) return a.giver;
    return null;
  }

  private makeOffer(): void {
    const { npcs, player, store, rng, ui } = this.s;
    const p = player.position;
    const near = npcs.agents.filter(
      (ag) =>
        FAVOR_GIVERS.includes(ag.def.id) &&
        ag.active &&
        ag.hold === null &&
        ag.state !== 'flee' &&
        ag.state !== 'talk' &&
        roomSlotAt(ag.position.x) < 0 &&
        ag.position.distanceTo(p) < OFFER_RANGE &&
        usedToday(store, `favor_from_${ag.def.id}`) < 2,
    );
    const options = near.flatMap((ag) =>
      favorsFrom(ag.def.id)
        .filter((f) => !store.doneToday(`favor_${f.id}`))
        .filter((f) => f.kind !== 'errand' || !!npcs.get(f.to ?? '')?.active)
        .map((def) => ({ def, agent: ag })),
    );
    if (!options.length) return;
    const pick = rng.pick(options);
    this.offer = { ...pick, until: this.s.now() + OFFER_SECONDS };
    ui.hud.bubble(
      pick.agent.def.id,
      rng.pick(['Hey! You got a sec?', "Psst! C'mere!", "Hey you! I'll pay ya!", 'Got a minute, hon?']),
      this.head(pick.agent),
      4,
    );
  }

  /** Talking to whoever's waving you over (or whoever you're running a favor for). */
  private talk(agent: NpcAgent): Scene | null {
    const { store } = this.s;
    const speaker = { id: agent.def.id, name: agent.def.title, voice: agent.def.voice };
    const a = this.active;
    if (a) {
      const name = a.giver.def.name;
      if (a.def.kind === 'beer' && agent === a.giver && store.has('beer'))
        return this.handOver(agent, `You hand ${name} a cold one.\n\n${a.def.thanks}`, () =>
          store.take('beer'),
        );
      if (a.def.kind === 'find' && a.stage === 'holding' && agent === a.giver)
        return this.handOver(agent, `You hand over the ${a.def.item?.name}.\n\n${a.def.thanks}`);
      if (a.def.kind === 'errand' && agent.def.id === a.def.to)
        return this.handOver(
          agent,
          `You hand over the ${a.def.item?.name} from ${name}.\n\n${a.def.toLine ?? ''}\n\n(${name} paid you for the errand.)`,
        );
      return null;
    }
    const o = this.offer;
    if (!o || o.agent !== agent) return null;
    const [lo, hi] = o.def.pay;
    const beerNow = o.def.kind === 'beer' && store.has('beer');
    return {
      title: agent.def.name,
      speaker,
      text: o.def.ask,
      choices: [
        beerNow
          ? {
              label: 'Hand over a cold one',
              hint: `-1 :beer: · +$${lo}–${hi}`,
              run: () => {
                store.take('beer');
                return this.payNow(o.def, o.agent);
              },
            }
          : {
              label: o.def.kind === 'beer' ? "I'll go grab one" : 'Sure, I got you',
              hint: `+$${lo}–${hi}`,
              run: () => {
                this.accept();
              },
            },
        {
          label: 'Not right now',
          run: () => {
            this.clearOffer();
            this.timer = 30;
          },
        },
      ],
    };
  }

  /** A scene that pays you as it opens (handing over the beer, the teeth, the casserole). */
  private handOver(agent: NpcAgent, text: string, before?: () => void): Scene {
    before?.();
    const pay = this.active?.pay ?? 0;
    this.finish(false);
    this.s.sfx('cash');
    return {
      title: agent.def.name,
      speaker: { id: agent.def.id, name: agent.def.title, voice: agent.def.voice },
      text: `${text}\n\n+$${pay} · +1 Clout`,
      choices: [],
      leave: 'Anytime',
    };
  }

  /** Done before you even said yes (you had a beer on you). */
  private payNow(def: FavorDef, agent: NpcAgent): { text: string; money: number; clout: number } {
    const pay = this.s.rng.int(def.pay[0], def.pay[1]);
    this.clearOffer();
    agent.endTalk();
    this.count(def);
    this.timer = this.s.rng.range(12, 22);
    this.s.sfx('cash');
    return { text: def.thanks, money: pay, clout: 1 };
  }

  private accept(): void {
    const o = this.offer;
    if (!o) return;
    const { store, rng, world } = this.s;
    this.clearOffer();
    const def = o.def;
    const a: Active = {
      def,
      giver: o.agent,
      pay: rng.int(def.pay[0], def.pay[1]),
      deadline: store.now + FAVOR_MINUTES,
      stage: def.kind === 'errand' ? 'holding' : 'start',
      prop: null,
      sparkle: null,
      spot: null,
    };
    if (def.kind === 'find') {
      const spot = this.hideSpot(o.agent.def.home);
      a.spot = spot;
      a.prop = lostThing(def.item?.name ?? '');
      a.prop.position.set(spot.x, 0, spot.z);
      a.prop.rotation.y = rng.range(0, Math.PI * 2);
      a.sparkle = glint();
      a.sparkle.position.set(spot.x, 0.45, spot.z);
      world.dynamic.add(a.prop, a.sparkle);
    } else if (def.kind === 'carry') {
      const from = def.from_at ?? this.besideGiver(o.agent);
      a.spot = from;
      a.prop = carryThing(def.item?.name ?? '');
      a.prop.position.set(from.x, 0, from.z);
      world.dynamic.add(a.prop);
    }
    this.active = a;
    o.agent.endTalk();
    store.toast(
      `Favor for ${o.agent.def.name}: ${this.objective()?.text ?? def.kind}`,
      'info',
      ':raise-hand:',
    );
  }

  /** Somewhere around their place, out in the open, that isn't the highway. */
  private hideSpot(home: Vec2): Vec2 {
    const { rng, world } = this.s;
    for (let i = 0; i < 30; i++) {
      const ang = rng.range(0, Math.PI * 2);
      const r = rng.range(3, 9);
      const x = home.x + Math.cos(ang) * r;
      const z = home.z + Math.sin(ang) * r;
      if (!inRect(PARK_BOUNDS, x, z) || inRect(HIGHWAY, x, z, 1) || world.collision.blocked(x, z, 0.6))
        continue;
      return { x, z };
    }
    return { x: home.x + 2, z: home.z + 2 };
  }

  private besideGiver(agent: NpcAgent): Vec2 {
    const p = this.s.player.position;
    const dx = p.x - agent.position.x;
    const dz = p.z - agent.position.z;
    const d = Math.hypot(dx, dz) || 1;
    const spot = { x: agent.position.x + (dx / d) * 1.5, z: agent.position.z + (dz / d) * 1.5 };
    this.s.world.collision.resolve(spot, 0.5);
    return spot;
  }

  private pickUp(): void {
    const a = this.active;
    const { player, store } = this.s;
    if (!a || a.stage !== 'start' || !a.prop) return;
    if (a.def.kind === 'find') {
      a.prop.removeFromParent();
      a.sparkle?.removeFromParent();
      a.prop = null;
      a.sparkle = null;
      a.spot = null;
      a.stage = 'holding';
      this.s.sfx('pickup');
      store.toast(
        `Found ${possessive(a.giver.def.name)} ${a.def.item?.name}! Take it back to them.`,
        'good',
        a.def.item?.icon,
      );
      return;
    }
    if (player.carrying || player.pushing) {
      store.toast('Your hands are full.', 'bad', ':box:');
      return;
    }
    player.setCarrying(a.prop);
    a.spot = null;
    a.stage = 'holding';
    this.s.sfx('thud');
  }

  private fix(): void {
    const a = this.active;
    const { player } = this.s;
    if (!a || a.def.kind !== 'fix' || player.busy) return;
    player.doBusy({
      pose: 'throw',
      until: this.s.now() + 0.9,
      onDone: () => {
        this.s.sfx('clank');
        this.s.effects.shake(0.15, 0.15);
        const at = a.def.at ?? player.position;
        for (let i = 0; i < 10; i++)
          this.s.world.sparks.spawn({
            x: at.x,
            y: 1.2,
            z: at.z,
            vx: (Math.random() - 0.5) * 3,
            vy: 1 + Math.random() * 2,
            vz: (Math.random() - 0.5) * 3,
            life: 0.5,
            size: 0.15,
            color: '#ffd27a',
            gravity: 6,
          });
        this.finish();
      },
    });
  }

  /** Done: get paid, and they say thanks (a bubble over their head, and a toast, unless a scene says it). */
  private finish(announce = true): void {
    const a = this.active;
    if (!a) return;
    const { store, ui } = this.s;
    this.count(a.def);
    store.apply({ money: a.pay, clout: 1 });
    if (announce) {
      const line = a.def.thanks.replace(/^'|'$/g, '');
      ui.hud.bubble(a.giver.def.id, line.length > 70 ? `${line.slice(0, 67)}…` : line, this.head(a.giver), 4);
      store.toast(`Favor done for ${a.giver.def.name}: +$${a.pay}`, 'good', ':raise-hand:');
    }
    this.drop();
    this.timer = this.s.rng.range(12, 22);
  }

  private count(def: FavorDef): void {
    const { store } = this.s;
    store.incr('favors');
    useToday(store, 'favors');
    useToday(store, `favor_from_${def.from}`);
    store.markToday(`favor_${def.id}`);
  }

  /** The objective pill while you're on a favor. */
  objective(): { text: string; target: THREE.Vector3 | null } | null {
    const a = this.active;
    if (!a) return null;
    const name = a.giver.def.name;
    const t = (v: Vec2 | THREE.Vector3): THREE.Vector3 => this.tmp.set(v.x, 0, v.z);
    const item = a.def.item?.name ?? '';
    switch (a.def.kind) {
      case 'beer':
        return {
          text: `:beer: Bring ${name} a cold one${this.s.store.has('beer') ? '' : " (you're out: fridge or Gas-N-Grub)"}`,
          target: t(a.giver.position),
        };
      case 'find':
        return a.stage === 'start'
          ? {
              text: `:magnifier: Find ${possessive(name)} ${item} (look for the sparkle)`,
              target: t(a.giver.def.home),
            }
          : { text: `:back: Bring ${name} the ${item}`, target: t(a.giver.position) };
      case 'carry':
        return a.stage === 'start'
          ? { text: `:box: Pick up the ${item}`, target: a.spot ? t(a.spot) : null }
          : {
              text: `:box: Carry the ${item} to ${a.def.place ?? 'the spot'}`,
              target: a.def.at ? t(a.def.at) : null,
            };
      case 'fix':
        return { text: `:wrench: ${a.def.verb ?? 'Fix it'}`, target: a.def.at ? t(a.def.at) : null };
      case 'errand': {
        const to = this.s.npcs.get(a.def.to ?? '');
        return {
          text: `${a.def.item?.icon ?? ':envelope:'} Take the ${item} to ${to?.def.name ?? 'them'}`,
          target: to ? t(to.position) : null,
        };
      }
    }
  }
}
